/**
 * Kriegsmarine Naval Grid data; coordinates are [latitude, longitude].
 *
 * This file is the heart of the Marinequadratkarte implementation: the cell
 * IDs, the corner coordinates, the irregular polygon shapes around Britain
 * and Iceland, and the antimeridian-crossing layout in the Pacific. Every
 * value here is reconstructed from Jan Kockrow's (GitHub: Nylle) original
 * research at navalgrid.com (https://www.navalgrid.com/) and his
 * cljs-navalgrid project (https://github.com/Nylle/cljs-navalgrid).
 *
 * Used with Jan's explicit written permission (email correspondence,
 * 2026-05-11). Reconstructing the Marinequadratkarte from primary archival
 * sources is an enormous amount of patient work, and none of this would
 * exist without it. If you build on this file, please visit navalgrid.com
 * and acknowledge Jan's work. See the package README for the full credit.
 */
export const largeRegularSquares = [
    { ids: ['ÄJ', 'ÄH', 'ÄG', 'ÄF'], nw: [85.2, -76], se: [77.1, -35.5] },
    { ids: ['ÄD', 'AA', 'AB', 'AC', 'AT'], nw: [77.1, -61], se: [69, -34] },
    { ids: ['ÄC', 'ÄB'], nw: [69, -85], se: [60.9, -64.75] },
    { ids: ['AE', 'AF'], nw: [69, -24.25], se: [60.9, -4] },
    { ids: ['AW', 'AU'], nw: [69, 29.75], se: [60.9, 50] },
    { ids: ['BA', 'BB', 'BC', 'BD', 'BE'], nw: [51, -79], se: [42.9, -65.5] },
    { ids: ['CA', 'CB', 'CC', 'CD', 'CE'], nw: [42.9, -78.7], se: [34.8, -67.9] },
    { ids: ['CG'], nw: [42.9, -15.1], se: [34.8, -4.3] },
    {
        ids: ['DA', 'DB', 'DC', 'DD', 'DE', 'DF', 'DG', 'DH', 'DJ'],
        nw: [34.8, -99.1],
        se: [26.7, -88.3],
    },
    {
        ids: ['DL', 'DM', 'DN', 'DO', 'DP', 'DQ', 'DR', 'DS', 'DT', 'DU'],
        nw: [26.7, -92.8],
        se: [18.6, -84.7],
    },
    {
        ids: ['PA', 'EA', 'EB', 'EC', 'ED', 'EE', 'EF', 'EG', 'EH', 'EJ', 'EK'],
        nw: [18.6, -100.9],
        se: [10.5, -92.8],
    },
    {
        ids: [
            'PB',
            'PC',
            'EL',
            'EM',
            'EN',
            'EO',
            'EP',
            'EQ',
            'ER',
            'ES',
            'ET',
            'EU',
            'EV',
            'EW',
        ],
        nw: [10.5, -100.9],
        se: [2.4, -92.8],
    },
    { ids: ['PD', 'PE', 'PF'], nw: [2.4, -100.9], se: [-5.7, -92.8] },
    {
        ids: ['FA', 'FB', 'FC', 'FD', 'FE', 'FF', 'FG', 'FH'],
        nw: [2.4, -52.3],
        se: [-5.7, -44.2],
    },
    {
        ids: ['FJ', 'FK', 'FL', 'FM', 'FN', 'FO', 'FP'],
        nw: [-5.7, -41.5],
        se: [-13.8, -33.4],
    },
    {
        ids: ['FQ', 'FR', 'FS', 'FT', 'FU', 'FV', 'FW'],
        nw: [-13.8, -41.5],
        se: [-21.9, -33.4],
    },
    {
        ids: ['GA', 'GB', 'GC', 'GD', 'GE', 'GF', 'GG', 'GH', 'GJ'],
        nw: [-21.9, -52.3],
        se: [-30, -44.2],
    },
    {
        ids: ['GK', 'GL', 'GM', 'GN', 'GO', 'GP', 'GQ', 'GR'],
        nw: [-30, -64.3],
        se: [-38.1, -53.5],
    },
    { ids: ['PT', 'PU'], nw: [-30, -90.7], se: [-38.1, -79.9] },
    {
        ids: ['PW', 'PX', 'GS', 'GT', 'GU', 'GV', 'GW', 'GX', 'GY', 'GZ'],
        nw: [-38.1, -90.7],
        se: [-46.2, -79.9],
    },
    {
        ids: ['PY', 'PZ', 'HA', 'HB', 'HC', 'HD', 'HE', 'HF', 'HG'],
        nw: [-46.2, -97],
        se: [-54.3, -83.5],
    },
    { ids: ['PG', 'PH', 'PJ'], nw: [-5.7, -100], se: [-13.8, -91.9] },
    { ids: ['PK', 'PL', 'PM', 'PN'], nw: [-13.8, -100], se: [-21.9, -91.9] },
    { ids: ['PO', 'PP', 'PQ', 'PR'], nw: [-21.9, -100], se: [-30, -91.9] },
    {
        ids: ['WW', 'HH', 'HJ', 'HK', 'HL', 'HM', 'HN'],
        nw: [-54.3, -92.2],
        se: [-62.4, -76],
    },
    { ids: ['XA', 'XC', 'XE'], nw: [82.4, 86], se: [74.3, 113] },
    { ids: ['XK', 'XL', 'XM'], nw: [82.4, 176], se: [74.3, -157] },
    { ids: ['XH', 'XO', 'XP', 'XQ'], nw: [74.3, -177.25], se: [66.2, -157] },
    { ids: ['XZ', 'XB', 'XD', 'XF'], nw: [74.3, 86], se: [66.2, 106.25] },
    { ids: ['NA', 'NB'], nw: [66.2, 134.6], se: [58.1, 150.8] },
    { ids: ['ND', 'NE', 'NF', 'NG'], nw: [66.2, 170.6], se: [58.1, -173.2] },
    { ids: ['NH', 'NJ', 'NK'], nw: [58.1, 126.5], se: [50, 140] },
    { ids: ['NM', 'NN', 'NO', 'NP', 'NQ'], nw: [58.1, 176], se: [50, -170.5] },
    { ids: ['NR', 'NS', 'NT'], nw: [50, 126.5], se: [41.9, 140] },
    { ids: ['NV', 'NW', 'NX', 'NY', 'NZ'], nw: [50, 176], se: [41.9, -170.5] },
    { ids: ['OA', 'OB', 'OC', 'OD', 'OE'], nw: [41.9, 113], se: [33.8, 123.8] },
    {
        ids: ['OG', 'OH', 'OJ', 'OK', 'OL', 'OM', 'ON'],
        nw: [41.9, 170.6],
        se: [33.8, -178.6],
    },
    { ids: ['OO', 'OP', 'OQ', 'OR', 'OS'], nw: [33.8, 113], se: [25.7, 123.8] },
    {
        ids: ['OU', 'OV', 'OW', 'OX', 'OY', 'OZ', 'QA', 'QB'],
        nw: [33.8, 170.6],
        se: [25.7, -178.6],
    },
    {
        ids: [
            'CR',
            'MD',
            'ME',
            'MF',
            'MG',
            'MH',
            'MJ',
            'MK',
            'ML',
            'MM',
            'MN',
            'QC',
            'QD',
            'QE',
            'QF',
            'QG',
            'QH',
        ],
        nw: [25.7, 29.3],
        se: [17.6, 37.4],
    },
    {
        ids: ['QK', 'QL', 'QM', 'QN', 'QO', 'QP', 'QQ', 'QR', 'QS', 'QT'],
        nw: [25.7, 170.6],
        se: [17.6, 178.7],
    },
    {
        ids: [
            'MO',
            'MP',
            'MQ',
            'MR',
            'MS',
            'MT',
            'MU',
            'MV',
            'MW',
            'MX',
            'QV',
            'QW',
            'QX',
            'QY',
            'QZ',
            'RA',
        ],
        nw: [17.6, 37.4],
        se: [9.5, 45.5],
    },
    {
        ids: [
            'MY',
            'MZ',
            'LA',
            'LB',
            'LC',
            'LD',
            'LE',
            'LF',
            'LG',
            'LH',
            'RO',
            'RP',
            'RQ',
            'RR',
            'RS',
            'RT',
        ],
        nw: [9.5, 37.4],
        se: [1.4, 45.5],
    },
    {
        ids: [
            'LJ',
            'LK',
            'LL',
            'LM',
            'LN',
            'LO',
            'LP',
            'LQ',
            'LR',
            'LS',
            'SG',
            'SH',
            'SJ',
            'SK',
            'SL',
            'SM',
        ],
        nw: [1.4, 37.4],
        se: [-6.7, 45.5],
    },
    {
        ids: [
            'LT',
            'LU',
            'LV',
            'LW',
            'LX',
            'LY',
            'LZ',
            'KA',
            'KB',
            'KC',
            'SZ',
            'TA',
            'TB',
            'TC',
            'TD',
            'TE',
        ],
        nw: [-6.7, 37.4],
        se: [-14.8, 45.5],
    },
    {
        ids: [
            'KD',
            'KE',
            'KF',
            'KG',
            'KH',
            'KJ',
            'KK',
            'KL',
            'KM',
            'KN',
            'KO',
            'TT',
            'TU',
            'TV',
            'TW',
            'TX',
            'TY',
        ],
        nw: [-14.8, 29.3],
        se: [-22.9, 37.4],
    },
    {
        ids: [
            'KP',
            'KQ',
            'KR',
            'KS',
            'KT',
            'KU',
            'KV',
            'KW',
            'KX',
            'UN',
            'UO',
            'UP',
            'UQ',
        ],
        nw: [-22.9, 26.6],
        se: [-31, 37.4],
    },
    {
        ids: [
            'KZ',
            'JA',
            'JB',
            'JC',
            'JD',
            'JE',
            'JF',
            'JG',
            'JH',
            'VB',
            'VC',
            'VD',
            'VE',
        ],
        nw: [-31, 26.6],
        se: [-39.1, 37.4],
    },
    {
        ids: ['RC', 'RD', 'RE', 'RF', 'RG', 'RH', 'RJ', 'RK', 'RL', 'RM'],
        nw: [17.6, 170.6],
        se: [9.5, 178.7],
    },
    {
        ids: ['RV', 'RW', 'RX', 'RY', 'RZ', 'SA', 'SB', 'SC', 'SD', 'SE'],
        nw: [9.5, 170.6],
        se: [1.4, 178.7],
    },
    {
        ids: ['SO', 'SP', 'SQ', 'SR', 'SS', 'ST', 'SU', 'SV', 'SW', 'SX'],
        nw: [1.4, 170.6],
        se: [-6.7, 178.7],
    },
    {
        ids: ['TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO', 'TP', 'TQ'],
        nw: [-6.7, 170.6],
        se: [-14.8, 178.7],
    },
    {
        ids: ['UA', 'UB', 'UC', 'UD', 'UE', 'UF', 'UG', 'UH', 'UJ', 'UK'],
        nw: [-14.8, 170.6],
        se: [-22.9, 178.7],
    },
    {
        ids: ['US', 'UT', 'UU', 'UV', 'UW', 'UX', 'UY', 'UZ'],
        nw: [-22.9, 170.6],
        se: [-31, -178.6],
    },
    {
        ids: ['VG', 'VH', 'VJ', 'VK', 'VL', 'VM', 'VN', 'VO'],
        nw: [-31, 170.6],
        se: [-39.1, -178.6],
    },
    {
        ids: ['JL', 'JM', 'JN', 'JO', 'JP', 'JQ', 'JR', 'VQ', 'VR', 'VS'],
        nw: [-39.1, 32],
        se: [-47.2, 45.5],
    },
    {
        ids: ['JT', 'JU', 'JV', 'JW', 'JX', 'JY', 'JZ', 'WB', 'WC', 'WD'],
        nw: [-47.2, 32],
        se: [-55.3, 45.5],
    },
    {
        ids: ['HO', 'HP', 'HQ', 'HR', 'HS', 'HT', 'HU', 'WO', 'WP'],
        nw: [-55.3, 21.2],
        se: [-63.4, 37.4],
    },
    {
        ids: ['VU', 'VV', 'VW', 'VX', 'VY', 'VZ'],
        nw: [-39.1, 176],
        se: [-47.2, -170.5],
    },
    {
        ids: ['WF', 'WG', 'WH', 'WJ', 'WK', 'WL'],
        nw: [-47.2, 176],
        se: [-55.3, -170.5],
    },
    { ids: ['WR', 'WS', 'WT', 'WU'], nw: [-55.3, -173.2], se: [-63.4, -157] },
    { ids: ['CO', 'CP'], nw: [37.5, 17.3], se: [29.4, 28.1] },
    { ids: ['MB', 'MC'], nw: [33.8, 48.2], se: [25.7, 59] },
    { ids: ['CU'], nw: [50, 45.5], se: [41.9, 59] },
    { ids: ['CV'], nw: [41.9, 48.2], se: [33.8, 59] },
    { ids: ['CK'], nw: [45.6, 17.3], se: [37.5, 28.1] },
    { ids: ['WQ'], nw: [-55.3, 167], se: [-63.4, -173.2] },
];
export const largePartialSquares = [
    {
        ids: ['OF', 'OT'],
        nw: [41.9, 167],
        se: [33.8, 170.6],
        sub: [[1], [4], [7]],
        o: 'v',
    },
    {
        ids: ['UR', 'VF'],
        nw: [-22.9, 167],
        se: [-31, 170.6],
        sub: [[1], [4], [7]],
        o: 'v',
    },
    {
        ids: ['NL', 'NU'],
        nw: [58.1, 167],
        se: [50, 176],
        sub: [
            [1, 2],
            [4, 5],
            [7, 8],
        ],
        o: 'v',
    },
    {
        ids: ['VT', 'WE'],
        nw: [-39.1, 167],
        se: [-47.2, 176],
        sub: [
            [1, 2],
            [4, 5],
            [7, 8],
        ],
        o: 'v',
    },
    {
        ids: ['JJ'],
        nw: [-38.1, 17.3],
        se: [-46.2, 24.5],
        sub: [
            [1, 2],
            [4, 5],
            [7, 8],
        ],
        o: 'v',
    },
    {
        ids: ['XJ'],
        nw: [82.4, 167],
        se: [74.3, 176],
        sub: [[1], [4], [7]],
        o: 'v',
    },
    {
        ids: ['AG'],
        nw: [69, 16.25],
        se: [60.9, 29.75],
        sub: [
            [1, 2],
            [4, 5],
            [7, 8],
        ],
        o: 'v',
    },
    { ids: ['CM'], nw: [40.2, 28.1], se: [37.5, 38.9], sub: [[1, 2, 3]], o: 'v' },
    {
        ids: ['YC'],
        nw: [85.1, 126.5],
        se: [82.4, -172.75],
        sub: [[7, 8, 9]],
        o: 'v',
    },
];
export const irregularSquares = [
    { ids: ['ÄA'], nw: [60.9, -71.5], se: [59.1, -44.5], o: 'v' },
    { ids: ['ÄE'], nw: [77.1, -85], se: [69, -61], o: 'v' },
    { ids: ['AD9'], nw: [60.9, -44.5], se: [59.1, -37.3], o: 'v' },
    { ids: ['AO1'], nw: [60.9, 7.7], se: [59.1, 14.9], o: 'h' },
    { ids: ['AO3'], nw: [60.9, 23.9], se: [59.1, 31.1], o: 'h' },
    { ids: ['AQ1', 'AQ2'], nw: [60.9, 40.1], se: [59.1, 47.3], o: 'h' },
    { ids: ['AQ3'], nw: [60.9, 54.5], se: [59.1, 58.1], o: 'h' },
    { ids: ['AR'], nw: [69, 70.25], se: [60.9, 86], o: 'v' },
    { ids: ['AS'], nw: [77.1, 74], se: [69, 86], o: 'v' },
    { ids: ['BK7'], nw: [49.2, 36.5], se: [47.4, 42.5], o: 'h' },
    { ids: ['BK6'], nw: [51, 47], se: [50, 51.5], o: 'h' },
    {
        ids: ['BK56', 'BK64', 'BK65', 'BK66'],
        nw: [50.1, 45.5],
        se: [50, 47],
        o: 'h',
    },
    { ids: ['CF'], nw: [42.9, -24.7], se: [34.8, -15.1], o: 'h' },
    { ids: ['CL3'], nw: [47.4, 36.5], se: [45.6, 42.5], o: 'h' },
    {
        ids: [
            'CQ34',
            'CQ35',
            'CQ36',
            'CQ44',
            'CQ45',
            'CQ46',
            'CQ54',
            'CQ55',
            'CQ56',
        ],
        nw: [25.8, 31.4],
        se: [25.7, 32.3],
        o: 'h',
    },
    { ids: ['CQ3', 'CQ4', 'CQ5'], nw: [26.7, 31.4], se: [25.7, 34.1], o: 'h' },
    {
        ids: [
            'CS232',
            'CS235',
            'CS238',
            'CS262',
            'CS265',
            'CS268',
            'CS292',
            'CS295',
            'CS298',
            'CS332',
            'CS335',
            'CS338',
            'CS362',
            'CS365',
            'CS368',
            'CS392',
            'CS395',
            'CS398',
        ],
        nw: [45.6, 45.3],
        se: [45.3, 45.5],
        o: 'v',
    },
    {
        ids: ['CS23', 'CS26', 'CS29', 'CS33', 'CS36', 'CS39'],
        nw: [45.6, 44.9],
        se: [44.7, 45.5],
        o: 'v',
    },
    { ids: ['CS2', 'CS3'], nw: [45.6, 42.5], se: [42.9, 45.5], o: 'v' },
    { ids: ['CT1'], nw: [40.2, 38.9], se: [39.2, 41], o: 'v' },
    { ids: ['CT2'], nw: [40.2, 41], se: [39.2, 44.6], o: 'v' },
    { ids: ['CT15'], nw: [40.2, 38.9], se: [40.1, 39.8], o: 'h' },
    {
        ids: ['CT16', 'CT24', 'CT25', 'CT26'],
        nw: [40.2, 39.8],
        se: [40.1, 41],
        o: 'h',
    },
    { ids: ['CT31'], nw: [41.9, 45.5], se: [41, 45.8], o: 'v' },
    { ids: ['CT4', 'CT7'], nw: [39.2, 38.9], se: [36.5, 41], o: 'v' },
    { ids: ['CT157'], nw: [40.2, 38.9], se: [40.1, 39], o: 'v' },
    { ids: ['CT1579'], nw: [40.2, 38.9], se: [40.1, 39], o: 'v' },
    {
        ids: ['CT313', 'CT316', 'CT319', 'CT343', 'CT346'],
        nw: [41.9, 45.5],
        se: [41.6, 45.8],
        o: 'v',
    },
    { ids: ['DK'], nw: [26.7, -100], se: [18.6, -92.8], o: 'v' },
    { ids: ['JK'], nw: [-39.1, 24.5], se: [-47.2, 32], o: 'v' },
    { ids: ['JS7'], nw: [-54.3, 21.2], se: [-55.3, 23], o: 'v' },
    { ids: ['JS75'], nw: [-54.3, 21.2], se: [-54.4, 21.5], o: 'h' },
    { ids: ['JS759'], nw: [-54.3, 21.2], se: [-54.4, 21.5], o: 'h' },
    {
        ids: [
            'JS7598',
            'JS7832',
            'JS7835',
            'JS7838',
            'JS7862',
            'JS7865',
            'JS7868',
            'JS7892',
            'JS7895',
            'JS7898',
        ],
        nw: [-54.3, 21.2],
        se: [-54.4, 21.33333333],
        o: 'v',
    },
    { ids: ['JS76', 'JS84'], nw: [-54.3, 21.5], se: [-54.4, 23], o: 'h' },
    { ids: ['JS78'], nw: [-54.4, 21.2], se: [-55.3, 21.5], o: 'h' },
    {
        ids: ['JS783', 'JS786', 'JS789'],
        nw: [-54.4, 21.2],
        se: [-54.7, 21.5],
        o: 'v',
    },
    {
        ids: ['KY23', 'KY26', 'KY29', 'KY53', 'KY56', 'KY59', 'KY83'],
        nw: [-31, 22.1],
        se: [-31.9, 23],
        o: 'v',
    },
    { ids: ['KY2', 'KY5'], nw: [-31, 22.1], se: [-33.7, 23], o: 'v' },
    { ids: ['KY8'], nw: [-36.4, 22.1], se: [-38.1, 23], o: 'v' },
    { ids: ['KY86'], nw: [-37.3, 22.1], se: [-38.1, 23], o: 'v' },
    { ids: ['KY867'], nw: [-37.9, 22.1], se: [-38.1, 22.2], o: 'v' },
    { ids: ['KY8673', 'KY8676'], nw: [-37.9, 22.1], se: [-38, 22.2], o: 'v' },
    { ids: ['KY94'], nw: [-37.3, 23], se: [-38.1, 24.2], o: 'v' },
    { ids: ['KY9579'], nw: [-38.1, 24.5], se: [-38.2, 24.6], o: 'h' },
    { ids: ['KY98'], nw: [-38.2, 24.5], se: [-39.1, 25.4], o: 'v' },
    { ids: ['MA1', 'MA4'], nw: [33.8, 38.9], se: [31.1, 41], o: 'v' },
    {
        ids: [
            'CT18',
            'CT42',
            'CT45',
            'CT48',
            'CT72',
            'CT75',
            'CT78',
            'MA12',
            'MA15',
            'MA18',
            'MA42',
            'MA45',
            'MA48',
            'MA72',
        ],
        nw: [40.1, 38.9],
        se: [39.2, 39.8],
        o: 'v',
    },
    { ids: ['MA78'], nw: [26.6, 39.5], se: [25.7, 39.8], o: 'v' },
    { ids: ['MA757'], nw: [26.9, 38.9], se: [26.7, 39], o: 'v' },
    {
        ids: ['MA783', 'MA786', 'MA789'],
        nw: [26.6, 39.5],
        se: [26.3, 39.8],
        o: 'v',
    },
    { ids: ['NC'], nw: [66.2, 167], se: [58.1, 170.6], o: 'v' },
    { ids: ['PS', 'PV'], nw: [-30, -100], se: [-38.1, -90.7], o: 'v' },
    {
        ids: ['QJ', 'RB', 'RU', 'SN', 'TF', 'TZ'],
        nw: [25.7, 167],
        se: [17.6, 170.6],
        o: 'v',
    },
    { ids: ['QU96'], nw: [19.4, -100.9], se: [18.6, -100], o: 'v' },
    { ids: ['RN', 'SF'], nw: [17.6, -108.4], se: [9.5, -100.9], o: 'v' },
    { ids: ['SY96'], nw: [-5.7, -100.9], se: [-5.8, -100], o: 'v' },
    { ids: ['TR', 'UL'], nw: [-6.7, -108.4], se: [-14.8, -100], o: 'v' },
    { ids: ['VA'], nw: [-22.9, -103], se: [-30, -100], o: 'v' },
    { ids: ['VP', 'WA'], nw: [-30, -103], se: [-38.1, -100], o: 'v' },
    { ids: ['WM'], nw: [-46.2, -103], se: [-54.3, -97], o: 'v' },
    { ids: ['WV1'], nw: [-55.3, -108.4], se: [-57, -103], o: 'v' },
    {
        ids: ['WV14', 'WV15', 'WV16'],
        nw: [-55.3, -108.4],
        se: [-56.1, -106.6],
        o: 'h',
    },
    { ids: ['XG'], nw: [74.3, 167], se: [66.2, -177.25], o: 'v' },
    { ids: ['XN6'], nw: [79.7, -85], se: [77.1, -76], o: 'v' },
    { ids: ['XN67', 'XN68', 'XN69'], nw: [77.9, -85], se: [77.1, -82], o: 'h' },
    { ids: ['XR'], nw: [74.3, -96.25], se: [66.2, -85], o: 'v' },
    { ids: ['YB'], nw: [85.1, 86], se: [82.4, 126.5], o: 'v' },
    { ids: ['YD'], nw: [85.1, -172.75], se: [82.4, -116.5], o: 'v' },
    { ids: ['YD9'], nw: [85.1, -132.25], se: [82.4, -116.5], o: 'v' },
    { ids: ['YE'], nw: [85.1, -116.5], se: [82.4, -76], o: 'v' },
];
export const polygonalSquares = [
    {
        id: 'AD',
        poly: [
            [69, -37.75],
            [69, -24.25],
            [60.9, -24.25],
            [60.9, -37.3],
            [59.1, -37.3],
            [59.1, -44.5],
            [66.3, -44.5],
            [66.3, -37.75],
        ],
    },
    {
        id: 'AH',
        poly: [
            [59.1, -69.7],
            [59.1, -53.5],
            [51, -53.5],
            [51, -67],
            [56.4, -67],
            [56.4, -69.7],
        ],
    },
    {
        id: 'AJ',
        poly: [
            [59.1, -53.5],
            [59.1, -37.3],
            [56.4, -37.3],
            [56.4, -40],
            [51, -40],
            [51, -53.5],
        ],
    },
    {
        id: 'AK',
        poly: [
            [60.9, -37.3],
            [60.9, -26.5],
            [51, -26.5],
            [51, -40],
            [56.4, -40],
            [56.4, -37.3],
        ],
    },
    {
        id: 'AL',
        poly: [
            [60.9, -26.5],
            [60.9, -15.7],
            [56.4, -15.7],
            [56.4, -16],
            [51, -16],
            [51, -26.5],
        ],
    },
    {
        id: 'AM',
        poly: [
            [60.9, -15.7],
            [60.9, -4.9],
            [56.4, -4.9],
            [56.4, -4],
            [55.5, -4],
            [55.5, -2.5],
            [51, -2.5],
            [51, -16],
            [56.4, -16],
            [56.4, -15.7],
        ],
    },
    {
        id: 'AM6',
        poly: [
            [56.4, -7],
            [56.4, -4],
            [55.5, -4],
            [55.5, -2.5],
            [53.7, -2.5],
            [53.7, -7],
        ],
    },
    {
        id: 'AN',
        poly: [
            [60.9, -4.9],
            [60.9, 7.7],
            [59.1, 7.7],
            [59.1, 9.5],
            [51.9, 9.5],
            [51.9, 6.5],
            [51, 6.5],
            [51, -2.5],
            [55.5, -2.5],
            [55.5, -4],
            [56.4, -4],
            [56.4, -4.9],
        ],
    },
    {
        id: 'AN5',
        poly: [
            [56.4, -4],
            [56.4, 2],
            [53.7, 2],
            [53.7, -2.5],
            [55.5, -2.5],
            [55.5, -4],
        ],
    },
    {
        id: 'AO',
        poly: [
            [60.9, 7.7],
            [60.9, 31.1],
            [59.1, 31.1],
            [59.1, 25.7],
            [56.4, 25.7],
            [56.4, 21.5],
            [53.7, 21.5],
            [53.7, 17],
            [52.8, 17],
            [52.8, 9.5],
            [59.1, 9.5],
            [59.1, 7.7],
        ],
    },
    {
        id: 'AO7',
        poly: [
            [56.4, 9.5],
            [56.4, 14],
            [53.7, 14],
            [53.7, 11],
            [52.8, 11],
            [52.8, 9.5],
        ],
    },
    {
        id: 'AO8',
        poly: [
            [56.4, 14],
            [56.4, 17],
            [52.8, 17],
            [52.8, 11],
            [53.7, 11],
            [53.7, 14],
        ],
    },
    {
        id: 'AP',
        poly: [
            [60.9, 31.1],
            [60.9, 40.1],
            [59.1, 40.1],
            [59.1, 41.9],
            [56.4, 41.9],
            [56.4, 44],
            [53.7, 44],
            [53.7, 21.5],
            [56.4, 21.5],
            [56.4, 25.7],
            [59.1, 25.7],
            [59.1, 31.1],
        ],
    },
    {
        id: 'AQ',
        poly: [
            [60.9, 40.1],
            [60.9, 58.1],
            [56.4, 58.1],
            [56.4, 57.5],
            [53.7, 57.5],
            [53.7, 44],
            [56.4, 44],
            [56.4, 41.9],
            [59.1, 41.9],
            [59.1, 40.1],
        ],
    },
    {
        id: 'BF',
        poly: [
            [51, -11.5],
            [51, 3.5],
            [50.1, 3.5],
            [50.1, 2],
            [49.2, 2],
            [49.2, 0.5],
            [48.3, 0.5],
            [48.3, -1],
            [45.6, -1],
            [45.6, -0.7],
            [42.9, -0.7],
            [42.9, -11.5],
        ],
    },
    {
        id: 'BF3',
        poly: [
            [51, -2.5],
            [51, 3.5],
            [50.1, 3.5],
            [50.1, 2],
            [49.2, 2],
            [49.2, 0.5],
            [48.3, 0.5],
            [48.3, -2.5],
        ],
    },
    {
        id: 'BG',
        poly: [
            [51, 3.5],
            [51, 6.5],
            [47.4, 6.5],
            [47.4, 11],
            [45.6, 11],
            [45.6, 10.1],
            [44.7, 10.1],
            [44.7, 6.5],
            [43.8, 6.5],
            [43.8, 2.9],
            [42.9, 2.9],
            [42.9, -0.7],
            [45.6, -0.7],
            [45.6, -1],
            [48.3, -1],
            [48.3, 0.5],
            [49.2, 0.5],
            [49.2, 2],
            [50.1, 2],
            [50.1, 3.5],
        ],
    },
    {
        id: 'BG1',
        poly: [
            [51, 3.5],
            [51, 6.5],
            [48.3, 6.5],
            [48.3, 0.5],
            [49.2, 0.5],
            [49.2, 2],
            [50.1, 2],
            [50.1, 3.5],
        ],
    },
    {
        id: 'BG4',
        poly: [
            [47.4, 6.5],
            [47.4, 11],
            [45.6, 11],
            [45.6, 10.1],
            [44.7, 10.1],
            [44.7, 6.5],
        ],
    },
    {
        id: 'BH',
        poly: [
            [53.7, 17],
            [53.7, 23],
            [45.6, 23],
            [45.6, 11],
            [47.4, 11],
            [47.4, 6.5],
            [51.9, 6.5],
            [51.9, 9.5],
            [52.8, 9.5],
            [52.8, 17],
        ],
    },
    {
        id: 'BH2',
        poly: [
            [53.7, 17],
            [53.7, 18.5],
            [50.1, 18.5],
            [50.1, 14],
            [52.8, 14],
            [52.8, 17],
        ],
    },
    {
        id: 'BJ',
        poly: [
            [53.7, 23],
            [53.7, 36.5],
            [47.4, 36.5],
            [47.4, 30.5],
            [46.5, 30.5],
            [46.5, 29],
            [45.6, 29],
            [45.6, 23],
        ],
    },
    {
        id: 'BJ8',
        poly: [
            [48.3, 27.5],
            [48.3, 32],
            [47.4, 32],
            [47.4, 30.5],
            [46.5, 30.5],
            [46.5, 29],
            [45.6, 29],
            [45.6, 27.5],
        ],
    },
    {
        id: 'BK',
        poly: [
            [53.7, 36.5],
            [53.7, 51.5],
            [50, 51.5],
            [50, 45.5],
            [45.6, 45.5],
            [45.6, 42.5],
            [47.4, 42.5],
            [47.4, 36.5],
        ],
    },
    {
        id: 'BK5',
        poly: [
            [51, 42.5],
            [51, 47],
            [50, 47],
            [50, 45.5],
            [48.3, 45.5],
            [48.3, 42.5],
        ],
    },
    {
        id: 'CH',
        poly: [
            [43.8, 2.9],
            [43.8, 6.5],
            [35.7, 6.5],
            [35.7, 2.9],
            [34.8, 2.9],
            [34.8, -4.3],
            [42.9, -4.3],
            [42.9, 2.9],
        ],
    },
    {
        id: 'CJ',
        poly: [
            [45.6, 10.1],
            [45.6, 17.3],
            [37.5, 17.3],
            [37.5, 10.1],
            [36.6, 10.1],
            [36.6, 6.5],
            [44.7, 6.5],
            [44.7, 10.1],
        ],
    },
    {
        id: 'CL',
        poly: [
            [47.4, 30.5],
            [47.4, 42.5],
            [45.6, 42.5],
            [45.6, 38.9],
            [44.7, 38.9],
            [44.7, 41.3],
            [40.2, 41.3],
            [40.2, 28.1],
            [45.6, 28.1],
            [45.6, 29],
            [46.5, 29],
            [46.5, 30.5],
        ],
    },
    {
        id: 'CL2',
        poly: [
            [47.4, 30.5],
            [47.4, 36.5],
            [45.6, 36.5],
            [45.6, 29],
            [46.5, 29],
            [46.5, 30.5],
        ],
    },
    {
        id: 'CN',
        poly: [
            [37.5, 10.1],
            [37.5, 17.3],
            [29.4, 17.3],
            [29.4, 6.5],
            [36.6, 6.5],
            [36.6, 10.1],
        ],
    },
    {
        id: 'CQ',
        poly: [
            [29.4, 31.7],
            [29.4, 38.9],
            [26.7, 38.9],
            [26.7, 39.5],
            [25.7, 39.5],
            [25.7, 31.4],
            [26.7, 31.4],
            [26.7, 31.7],
        ],
    },
    {
        id: 'CS',
        poly: [
            [45.6, 38.9],
            [45.6, 45.5],
            [40.2, 45.5],
            [40.2, 41.3],
            [44.7, 41.3],
            [44.7, 38.9],
        ],
    },
    {
        id: 'CS1',
        poly: [
            [45.6, 38.9],
            [45.6, 42.5],
            [40.2, 42.5],
            [40.2, 41.3],
            [44.7, 41.3],
            [44.7, 38.9],
        ],
    },
    {
        id: 'CT',
        poly: [
            [41.9, 45.5],
            [41.9, 48.2],
            [33.8, 48.2],
            [33.8, 38.9],
            [40.2, 38.9],
            [40.2, 45.5],
        ],
    },
    {
        id: 'CT3',
        poly: [
            [41.9, 45.5],
            [41.9, 48.2],
            [39.2, 48.2],
            [39.2, 44.6],
            [40.2, 44.6],
            [40.2, 45.5],
        ],
    },
    {
        id: 'CT34',
        poly: [
            [41, 45.5],
            [41, 45.8],
            [40.1, 45.8],
            [40.1, 44.6],
            [40.2, 44.6],
            [40.2, 45.5],
        ],
    },
    {
        id: 'CT349',
        poly: [
            [40.4, 45.5],
            [40.4, 45.8],
            [40.1, 45.8],
            [40.1, 45.4],
            [40.2, 45.4],
            [40.2, 45.5],
        ],
    },
    {
        id: 'JS',
        poly: [
            [-47.2, 24.5],
            [-47.2, 32],
            [-55.3, 32],
            [-55.3, 21.2],
            [-54.3, 21.2],
            [-54.3, 24.5],
        ],
    },
    {
        id: 'JS8',
        poly: [
            [-52.6, 24.5],
            [-52.6, 27.5],
            [-55.3, 27.5],
            [-55.3, 23],
            [-54.3, 23],
            [-54.3, 24.5],
        ],
    },
    {
        id: 'KY',
        poly: [
            [-31, 22.1],
            [-31, 26.6],
            [-39.1, 26.6],
            [-39.1, 24.5],
            [-38.1, 24.5],
            [-38.1, 22.1],
        ],
    },
    {
        id: 'KY9',
        poly: [
            [-36.4, 23],
            [-36.4, 26.6],
            [-39.1, 26.6],
            [-39.1, 24.5],
            [-38.1, 24.5],
            [-38.1, 23],
        ],
    },
    {
        id: 'KY95',
        poly: [
            [-37.3, 24.2],
            [-37.3, 25.4],
            [-38.2, 25.4],
            [-38.2, 24.5],
            [-38.1, 24.5],
            [-38.1, 24.2],
        ],
    },
    {
        id: 'KY957',
        poly: [
            [-37.9, 24.2],
            [-37.9, 24.6],
            [-38.2, 24.6],
            [-38.2, 24.5],
            [-38.1, 24.5],
            [-38.1, 24.2],
        ],
    },
    {
        id: 'MA',
        poly: [
            [33.8, 38.9],
            [33.8, 48.2],
            [25.7, 48.2],
            [25.7, 39.5],
            [26.7, 39.5],
            [26.7, 38.9],
        ],
    },
    {
        id: 'MA7',
        poly: [
            [28.4, 38.9],
            [28.4, 41],
            [25.7, 41],
            [25.7, 39.5],
            [26.7, 39.5],
            [26.7, 38.9],
        ],
    },
    {
        id: 'MA75',
        poly: [
            [27.5, 38.9],
            [27.5, 39.8],
            [26.6, 39.8],
            [26.6, 39.5],
            [26.7, 39.5],
            [26.7, 38.9],
        ],
    },
    {
        id: 'MA759',
        poly: [
            [26.9, 39.4],
            [26.9, 39.8],
            [26.6, 39.8],
            [26.6, 39.5],
            [26.7, 39.5],
            [26.7, 39.4],
        ],
    },
    {
        id: 'QU',
        poly: [
            [25.7, -108.4],
            [25.7, -100],
            [18.6, -100],
            [18.6, -100.9],
            [17.6, -100.9],
            [17.6, -108.4],
        ],
    },
    {
        id: 'QU9',
        poly: [
            [20.3, -102.7],
            [20.3, -100],
            [18.6, -100],
            [18.6, -100.9],
            [17.6, -100.9],
            [17.6, -102.7],
        ],
    },
    {
        id: 'SY',
        poly: [
            [1.4, -108.4],
            [1.4, -100.9],
            [-5.7, -100.9],
            [-5.7, -100],
            [-6.7, -100],
            [-6.7, -108.4],
        ],
    },
    {
        id: 'SY9',
        poly: [
            [-4, -102.7],
            [-4, -100.9],
            [-5.8, -100.9],
            [-5.8, -100],
            [-6.7, -100],
            [-6.7, -102.7],
        ],
    },
    {
        id: 'WV',
        poly: [
            [-54.3, -103],
            [-54.3, -92.2],
            [-62.4, -92.2],
            [-62.4, -108.4],
            [-55.3, -108.4],
            [-55.3, -103],
        ],
    },
    {
        id: 'XN',
        poly: [
            [82.4, -103],
            [82.4, -76],
            [77.1, -76],
            [77.1, -85],
            [74.3, -85],
            [74.3, -103],
        ],
    },
];
export const twoByFiveSquares = [
    { ids: ['ÄA1', 'ÄA2', 'ÄA3'], nw: [60.9, -71.5], se: [59.1, -62.5], so: 'h' },
    {
        ids: ['AK1', 'AK2', 'AK3', 'AL1', 'AL2', 'AL3', 'AM1', 'AM2', 'AM3', 'AN1'],
        nw: [60.9, -37.3],
        se: [56.4, -33.7],
        so: 'v',
    },
    { ids: ['AN2'], nw: [60.9, -1.3], se: [59.1, 7.7], so: 'h' },
    { ids: ['AN9'], nw: [56.4, 6.5], se: [51.9, 9.5], so: 'v' },
    { ids: ['BH4'], nw: [51.9, 6.5], se: [47.4, 9.5], so: 'v' },
    { ids: ['BH7'], nw: [47.4, 11], se: [45.6, 18.5], so: 'h' },
    { ids: ['AO2'], nw: [60.9, 14.9], se: [59.1, 23.9], so: 'h' },
    { ids: ['CL9'], nw: [44.7, 38.9], se: [40.2, 41.3], so: 'v' },
    { ids: ['AP2'], nw: [60.9, 31.1], se: [59.1, 40.1], so: 'h' },
    { ids: ['BK1', 'BK2'], nw: [53.7, 36.5], se: [49.2, 39.5], so: 'v' },
];
export const partialSquares = [
    { ids: ['ÄE1', 'ÄE4', 'ÄE7'], nw: [77.1, -85], se: [74.4, -79], o: 'v' },
    { ids: ['ÄE2', 'ÄE5', 'ÄE8'], nw: [77.1, -79], se: [74.4, -70], o: 'v' },
    { ids: ['ÄE3', 'ÄE6', 'ÄE9'], nw: [77.1, -70], se: [74.4, -61], o: 'v' },
    { ids: ['AD1', 'AD2'], nw: [69, -37.75], se: [66.3, -31], o: 'h' },
    { ids: ['AD3', 'AD4', 'AD5'], nw: [66.3, -44.5], se: [63.6, -37.75], o: 'h' },
    { ids: ['AD6', 'AD7', 'AD8'], nw: [63.6, -44.5], se: [60.9, -37.75], o: 'h' },
    {
        ids: ['AD91', 'AD92', 'AD93', 'AD94'],
        nw: [60.9, -44.5],
        se: [60, -42.7],
        o: 'h',
    },
    {
        ids: ['AD95', 'AD96', 'AD97', 'AD98'],
        nw: [60, -44.5],
        se: [59.1, -42.7],
        o: 'h',
    },
    {
        ids: ['AH1', 'AH2', 'AH3', 'AJ1', 'AJ2', 'AJ3'],
        nw: [59.1, -69.7],
        se: [56.4, -64.3],
        o: 'h',
    },
    {
        ids: ['AH4', 'AH5', 'AH6', 'AJ4', 'AJ5', 'AJ6'],
        nw: [56.4, -67],
        se: [53.7, -62.5],
        o: 'h',
    },
    {
        ids: ['AH7', 'AH8', 'AH9', 'AJ7', 'AJ8', 'AJ9'],
        nw: [53.7, -67],
        se: [51, -62.5],
        o: 'h',
    },
    { ids: ['AK4', 'AK5', 'AK6'], nw: [56.4, -40], se: [53.7, -35.5], o: 'h' },
    { ids: ['AK7', 'AK8', 'AK9'], nw: [53.7, -40], se: [51, -35.5], o: 'h' },
    {
        ids: ['AL4', 'AL5'],
        nw: [56.4, -26.5],
        se: [53.7, -23.5],
        sub: [
            [1, 2],
            [4, 5],
            [7, 8],
        ],
        o: 'h',
    },
    { ids: ['AL6', 'AM4', 'AM5'], nw: [56.4, -20.5], se: [53.7, -16], o: 'h' },
    {
        ids: ['AL7', 'AL8'],
        nw: [53.7, -26.5],
        se: [51, -23.5],
        sub: [
            [1, 2],
            [4, 5],
            [7, 8],
        ],
        o: 'h',
    },
    {
        ids: ['AL9', 'AM7', 'AM8', 'AM9'],
        nw: [53.7, -20.5],
        se: [51, -16],
        o: 'h',
    },
    { ids: ['AM61', 'AM62'], nw: [56.4, -7], se: [55.5, -5.5], o: 'h' },
    { ids: ['AM64', 'AM65', 'AM66'], nw: [55.5, -7], se: [54.6, -5.5], o: 'h' },
    { ids: ['AM67', 'AM68', 'AM69'], nw: [54.6, -7], se: [53.7, -5.5], o: 'h' },
    {
        ids: ['AN05', 'AN51', 'AN52', 'AN53'],
        nw: [56.4, -4],
        se: [55.5, -2.5],
        o: 'h',
    },
    {
        ids: [
            'AN4',
            'AN3',
            'AO4',
            'AO5',
            'AO6',
            'AP1',
            'AP3',
            'AP6',
            'AQ4',
            'AQ5',
            'AQ6',
        ],
        nw: [59.1, -1.3],
        se: [56.4, 4.1],
        o: 'h',
    },
    { ids: ['AN54', 'AN55', 'AN56'], nw: [55.5, -2.5], se: [54.6, -1], o: 'h' },
    { ids: ['AN57', 'AN58', 'AN59'], nw: [54.6, -2.5], se: [53.7, -1], o: 'h' },
    { ids: ['AN6'], nw: [56.4, 2], se: [53.7, 6.5], o: 'h' },
    { ids: ['AN7', 'AN8'], nw: [53.7, -2.5], se: [51, 2], o: 'h' },
    {
        ids: ['AO07', 'AO87', 'AO88', 'AO89', 'AO08'],
        nw: [53.7, 9.5],
        se: [52.8, 11],
        o: 'h',
    },
    {
        ids: ['AO11', 'AO12', 'AO13', 'AO14'],
        nw: [60.9, 7.7],
        se: [60, 9.5],
        o: 'h',
    },
    {
        ids: ['AO15', 'AO16', 'AO17', 'AO18'],
        nw: [60, 7.7],
        se: [59.1, 9.5],
        o: 'h',
    },
    {
        ids: ['AO31', 'AO32', 'AO33', 'AO34'],
        nw: [60.9, 23.9],
        se: [60, 25.7],
        o: 'h',
    },
    {
        ids: ['AO35', 'AO36', 'AO37', 'AO38'],
        nw: [60, 23.9],
        se: [59.1, 25.7],
        o: 'h',
    },
    {
        ids: ['AO71', 'AO72', 'AO73', 'AO81', 'AO82'],
        nw: [56.4, 9.5],
        se: [55.5, 11],
        o: 'h',
    },
    {
        ids: ['AO74', 'AO75', 'AO76', 'AO83', 'AO84'],
        nw: [55.5, 9.5],
        se: [54.6, 11],
        o: 'h',
    },
    {
        ids: ['AO77', 'AO78', 'AO79', 'AO85', 'AO86'],
        nw: [54.6, 9.5],
        se: [53.7, 11],
        o: 'h',
    },
    {
        ids: ['AO9', 'AP4', 'AP5', 'AP7', 'AP8', 'AP9', 'AQ7', 'AQ8', 'AQ9'],
        nw: [56.4, 17],
        se: [53.7, 21.5],
        o: 'h',
    },
    {
        ids: [
            'AQ11',
            'AQ12',
            'AQ13',
            'AQ14',
            'AQ21',
            'AQ22',
            'AQ23',
            'AQ24',
            'AQ31',
            'AQ32',
        ],
        nw: [60.9, 40.1],
        se: [60, 41.9],
        o: 'h',
    },
    {
        ids: [
            'AQ15',
            'AQ16',
            'AQ17',
            'AQ18',
            'AQ25',
            'AQ26',
            'AQ27',
            'AQ28',
            'AQ34',
            'AQ35',
        ],
        nw: [60, 40.1],
        se: [59.1, 41.9],
        o: 'h',
    },
    { ids: ['AR1', 'AR4', 'AR7'], nw: [69, 70.25], se: [66.3, 77], o: 'v' },
    { ids: ['AR2', 'AR5', 'AR8'], nw: [69, 77], se: [66.3, 83.75], o: 'v' },
    {
        ids: ['AR3', 'AR6', 'AR9'],
        nw: [69, 83.75],
        se: [66.3, 86],
        sub: [[1], [4], [7]],
        o: 'v',
    },
    { ids: ['AS1', 'AS4', 'AS7'], nw: [77.1, 74], se: [74.4, 83], o: 'v' },
    {
        ids: ['AS2', 'AS5', 'AS8'],
        nw: [77.1, 83],
        se: [74.4, 86],
        sub: [[1], [4], [7]],
        o: 'v',
    },
    { ids: ['BF1', 'BF2'], nw: [51, -11.5], se: [48.3, -7], o: 'h' },
    {
        ids: ['BF31', 'BF32', 'BF33', 'BF34', 'BG11', 'BG12'],
        nw: [51, -2.5],
        se: [50.1, -1],
        o: 'h',
    },
    {
        ids: ['BF35', 'BF36', 'BF37', 'BG13', 'BG14', 'BG15'],
        nw: [50.1, -2.5],
        se: [49.2, -1],
        o: 'h',
    },
    {
        ids: ['BF38', 'BF39', 'BG16', 'BG17', 'BG18', 'BG19'],
        nw: [49.2, -2.5],
        se: [48.3, -1],
        o: 'h',
    },
    { ids: ['BF4'], nw: [48.3, -11.5], se: [45.6, -7], o: 'h' },
    {
        ids: ['BF5', 'BF6', 'BG2'],
        nw: [48.3, -7],
        se: [45.6, -4],
        sub: [
            [1, 2],
            [4, 5],
            [7, 8],
        ],
        o: 'h',
    },
    {
        ids: ['BF7', 'BF8', 'BF9', 'BG5'],
        nw: [45.6, -11.5],
        se: [42.9, -7.9],
        o: 'h',
    },
    { ids: ['BG3'], nw: [48.3, 2], se: [45.6, 6.5], o: 'h' },
    { ids: ['BG41', 'BG42', 'BG43'], nw: [47.4, 6.5], se: [46.5, 8], o: 'h' },
    { ids: ['BG44', 'BG45', 'BG46'], nw: [46.5, 6.5], se: [45.6, 8], o: 'h' },
    { ids: ['BG47', 'BG48', 'BG49'], nw: [45.6, 6.5], se: [44.7, 7.7], o: 'h' },
    {
        ids: ['BG6'],
        nw: [45.6, 2.9],
        se: [43.8, 6.5],
        sub: [
            [4, 5, 6],
            [7, 8, 9],
        ],
        o: 'h',
    },
    {
        ids: ['BH02', 'BH23', 'BH26', 'BH29'],
        nw: [53.7, 17],
        se: [52.8, 18.5],
        o: 'v',
    },
    { ids: ['BH1'], nw: [52.8, 9.5], se: [50.1, 14], o: 'h' },
    { ids: ['BH21', 'BH24', 'BH27'], nw: [52.8, 14], se: [51.9, 15.5], o: 'v' },
    { ids: ['BH22', 'BH25', 'BH28'], nw: [52.8, 15.5], se: [51.9, 17], o: 'v' },
    { ids: ['BH3', 'BJ1', 'BJ2', 'BJ3'], nw: [53.7, 18.5], se: [51, 23], o: 'h' },
    { ids: ['BH5', 'BH6'], nw: [50.1, 9.5], se: [47.4, 14], o: 'h' },
    { ids: ['BH8', 'BJ4', 'BJ5', 'BJ6'], nw: [51, 18.5], se: [48.3, 23], o: 'h' },
    { ids: ['BH9', 'BJ7'], nw: [48.3, 18.5], se: [45.6, 23], o: 'h' },
    { ids: ['BJ81', 'BJ82', 'BJ83'], nw: [48.3, 27.5], se: [47.4, 29], o: 'h' },
    { ids: ['BJ9'], nw: [48.3, 32], se: [47.4, 36.5], sub: [[7, 8, 9]], o: 'v' },
    { ids: ['BK3', 'BK4'], nw: [53.7, 42.5], se: [51, 47], o: 'h' },
    {
        ids: ['BK51', 'BK52', 'BK53', 'BK61', 'BK62', 'BK63'],
        nw: [51, 42.5],
        se: [50.1, 44],
        o: 'h',
    },
    { ids: ['BK54', 'BK55'], nw: [50.1, 42.5], se: [49.2, 44], o: 'h' },
    {
        ids: [
            'BK561',
            'BK562',
            'BK563',
            'BK641',
            'BK642',
            'BK643',
            'BK651',
            'BK652',
            'BK653',
            'BK661',
            'BK662',
            'BK663',
        ],
        nw: [50.1, 45.5],
        se: [50, 46],
        sub: [[7, 8, 9]],
        o: 'h',
    },
    {
        ids: ['BK71', 'BK72', 'BK73', 'BK74', 'BK57', 'BK58'],
        nw: [49.2, 36.5],
        se: [48.3, 38],
        o: 'h',
    },
    {
        ids: ['BK75', 'BK76', 'BK77', 'BK78'],
        nw: [48.3, 36.5],
        se: [47.4, 38],
        o: 'h',
    },
    {
        ids: ['BK8'],
        nw: [48.3, 42.5],
        se: [45.6, 45.5],
        sub: [
            [1, 2],
            [4, 5],
            [7, 8],
        ],
        o: 'h',
    },
    {
        ids: ['CF1', 'CF4', 'CF7'],
        nw: [42.9, -24.7],
        se: [40.2, -22.3],
        sub: [
            [2, 3],
            [5, 6],
            [8, 9],
        ],
        o: 'v',
    },
    { ids: ['CF2', 'CF5', 'CF8'], nw: [42.9, -22.3], se: [40.2, -18.7], o: 'v' },
    { ids: ['CF3', 'CF6', 'CF9'], nw: [42.9, -18.7], se: [40.2, -15.1], o: 'v' },
    { ids: ['CH1', 'CH4', 'CH7'], nw: [42.9, -4.3], se: [40.2, -0.7], o: 'v' },
    { ids: ['CH2', 'CH5', 'CH8'], nw: [42.9, -0.7], se: [40.2, 2.9], o: 'v' },
    { ids: ['CH3', 'CH6', 'CH9'], nw: [43.8, 2.9], se: [41.1, 6.5], o: 'v' },
    { ids: ['CJ1', 'CJ4', 'CJ7'], nw: [44.7, 6.5], se: [42, 10.1], o: 'v' },
    {
        ids: ['CJ2', 'CJ5', 'CJ8', 'CN2', 'CN5', 'CN8'],
        nw: [45.6, 10.1],
        se: [42.9, 13.7],
        o: 'v',
    },
    {
        ids: ['CJ3', 'CJ6', 'CJ9', 'CN3', 'CN6', 'CN9'],
        nw: [45.6, 13.7],
        se: [42.9, 17.3],
        o: 'v',
    },
    { ids: ['CL1', 'CL5', 'CL6'], nw: [45.6, 28.1], se: [42.9, 31.7], o: 'h' },
    {
        ids: [
            'BJ84',
            'BJ85',
            'CL21',
            'CL22',
            'CL23',
            'CL24',
            'CL31',
            'CL32',
            'CL33',
            'CL34',
        ],
        nw: [47.4, 27.5],
        se: [46.5, 29],
        o: 'h',
    },
    {
        ids: [
            'BJ87',
            'CL25',
            'CL26',
            'CL27',
            'CL28',
            'CL29',
            'CL35',
            'CL36',
            'CL37',
            'CL38',
        ],
        nw: [46.5, 27.5],
        se: [45.6, 29],
        o: 'h',
    },
    { ids: ['CL4', 'CL7', 'CL8'], nw: [42.9, 28.1], se: [40.2, 31.7], o: 'h' },
    {
        ids: ['CN1'],
        nw: [36.6, 6.5],
        se: [34.8, 10.1],
        sub: [
            [4, 5, 6],
            [7, 8, 9],
        ],
        o: 'v',
    },
    { ids: ['CN4', 'CN7'], nw: [34.8, 6.5], se: [32.1, 10.1], o: 'v' },
    { ids: ['CQ1', 'CQ2'], nw: [29.4, 31.7], se: [26.7, 35.3], o: 'h' },
    {
        ids: [
            'CQ31',
            'CQ32',
            'CQ33',
            'CQ41',
            'CQ42',
            'CQ43',
            'CQ51',
            'CQ52',
            'CQ53',
        ],
        nw: [26.7, 31.4],
        se: [25.8, 32.3],
        o: 'h',
    },
    {
        ids: [
            'CQ341',
            'CQ342',
            'CQ343',
            'CQ351',
            'CQ352',
            'CQ353',
            'CQ361',
            'CQ362',
            'CQ363',
            'CQ441',
            'CQ442',
            'CQ443',
            'CQ451',
            'CQ452',
            'CQ453',
            'CQ461',
            'CQ462',
            'CQ463',
            'CQ541',
            'CQ542',
            'CQ543',
            'CQ551',
            'CQ552',
            'CQ553',
            'CQ561',
            'CQ562',
            'CQ563',
        ],
        nw: [25.8, 31.4],
        se: [25.7, 31.7],
        sub: [[7, 8, 9]],
        o: 'h',
    },
    {
        ids: ['CS11', 'CS12', 'CS13', 'CS21', 'CS22'],
        nw: [45.6, 38.9],
        se: [44.7, 40.1],
        o: 'h',
    },
    {
        ids: ['CS14', 'CS15', 'CS16', 'CS17', 'CS18'],
        nw: [44.7, 41.3],
        se: [43.8, 42.5],
        o: 'v',
    },
    {
        ids: [
            'CS231',
            'CS234',
            'CS237',
            'CS261',
            'CS264',
            'CS267',
            'CS291',
            'CS294',
            'CS297',
            'CS331',
            'CS334',
            'CS337',
            'CS361',
            'CS364',
            'CS367',
            'CS391',
            'CS394',
            'CS397',
        ],
        nw: [45.6, 44.9],
        se: [45.3, 45.3],
        o: 'v',
    },
    {
        ids: [
            'CS2321',
            'CS2324',
            'CS2327',
            'CS2351',
            'CS2354',
            'CS2357',
            'CS2381',
            'CS2384',
            'CS2387',
            'CS2621',
            'CS2624',
            'CS2627',
            'CS2651',
            'CS2654',
            'CS2657',
            'CS2681',
            'CS2684',
            'CS2687',
            'CS2921',
            'CS2924',
            'CS2927',
            'CS2951',
            'CS2954',
            'CS2957',
            'CS2981',
            'CS2984',
            'CS2987',
            'CS3321',
            'CS3324',
            'CS3327',
            'CS3351',
            'CS3354',
            'CS3357',
            'CS3381',
            'CS3384',
            'CS3387',
            'CS3621',
            'CS3624',
            'CS3627',
            'CS3651',
            'CS3654',
            'CS3657',
            'CS3681',
            'CS3684',
            'CS3687',
            'CS3921',
            'CS3924',
            'CS3927',
            'CS3951',
            'CS3954',
            'CS3957',
            'CS3981',
            'CS3984',
            'CS3987',
        ],
        nw: [45.6, 45.3],
        se: [45.5, 45.4333333],
        o: 'v',
    },
    {
        ids: [
            'CS2322',
            'CS2325',
            'CS2328',
            'CS2352',
            'CS2355',
            'CS2358',
            'CS2382',
            'CS2385',
            'CS2388',
            'CS2622',
            'CS2625',
            'CS2628',
            'CS2652',
            'CS2655',
            'CS2658',
            'CS2682',
            'CS2685',
            'CS2688',
            'CS2922',
            'CS2925',
            'CS2928',
            'CS2952',
            'CS2955',
            'CS2958',
            'CS2982',
            'CS2985',
            'CS2988',
            'CS3322',
            'CS3325',
            'CS3328',
            'CS3352',
            'CS3355',
            'CS3358',
            'CS3382',
            'CS3385',
            'CS3388',
            'CS3622',
            'CS3625',
            'CS3628',
            'CS3652',
            'CS3655',
            'CS3658',
            'CS3682',
            'CS3685',
            'CS3688',
            'CS3922',
            'CS3925',
            'CS3928',
            'CS3952',
            'CS3955',
            'CS3958',
            'CS3982',
            'CS3985',
            'CS3988',
        ],
        nw: [45.6, 45.43333333],
        se: [45.5, 45.5],
        o: 'v',
    },
    {
        ids: ['CS24', 'CS27', 'CS31', 'CS34', 'CS37'],
        nw: [44.7, 42.5],
        se: [43.8, 43.7],
        o: 'v',
    },
    {
        ids: ['CS25', 'CS28', 'CS32', 'CS35', 'CS38'],
        nw: [44.7, 43.7],
        se: [43.8, 44.9],
        o: 'v',
    },
    {
        ids: ['CT27', 'CT28', 'CT29', 'CT37', 'CT38', 'CT39'],
        nw: [40.1, 41],
        se: [39.2, 42.2],
        o: 'h',
    },
    { ids: ['CT32', 'CT33'], nw: [41.9, 45.8], se: [41, 47], o: 'h' },
    { ids: ['CT35', 'CT36'], nw: [41, 45.8], se: [40.1, 47], o: 'h' },
    {
        ids: [
            'CT158',
            'CT159',
            'CT167',
            'CT168',
            'CT169',
            'CT247',
            'CT248',
            'CT249',
            'CT257',
            'CT258',
            'CT259',
            'CT267',
            'CT268',
            'CT269',
            'CT347',
            'CT348',
        ],
        nw: [40.2, 39],
        se: [40.1, 39.4],
        sub: [[1, 2, 3]],
        o: 'h',
    },
    {
        ids: [
            'CT3132',
            'CT3135',
            'CT3138',
            'CT3162',
            'CT3165',
            'CT3168',
            'CT3192',
            'CT3195',
            'CT3198',
            'CT3432',
            'CT3435',
            'CT3438',
            'CT3462',
            'CT3465',
            'CT3468',
            'CT3492',
            'CT3495',
            'CT3498',
        ],
        nw: [41.9, 45.53333333],
        se: [41.8, 45.6666666],
        o: 'v',
    },
    {
        ids: [
            'CT3133',
            'CT3136',
            'CT3139',
            'CT3163',
            'CT3166',
            'CT3169',
            'CT3193',
            'CT3196',
            'CT3199',
            'CT3433',
            'CT3436',
            'CT3439',
            'CT3463',
            'CT3466',
            'CT3469',
            'CT3493',
            'CT3496',
            'CT3499',
        ],
        nw: [41.9, 45.6666666],
        se: [41.8, 45.8],
        o: 'v',
    },
    { ids: ['CT3497'], nw: [40.2, 45.4], se: [40.1, 45.53333333], o: 'v' },
    {
        ids: [
            'CT3131',
            'CT3134',
            'CT3137',
            'CT3161',
            'CT3164',
            'CT3167',
            'CT3191',
            'CT3194',
            'CT3197',
            'CT3431',
            'CT3434',
            'CT3437',
            'CT3461',
            'CT3464',
            'CT3467',
            'CT3491',
            'CT3494',
        ],
        nw: [41.9, 45.5],
        se: [41.8, 45.53333333],
        o: 'v',
    },
    { ids: ['DK1', 'DK4', 'DK7'], nw: [26.7, -100], se: [24, -98.2], o: 'v' },
    { ids: ['DK2', 'DK5', 'DK8'], nw: [26.7, -98.2], se: [24, -95.5], o: 'v' },
    { ids: ['DK3', 'DK6', 'DK9'], nw: [26.7, -95.5], se: [24, -92.8], o: 'v' },
    {
        ids: ['JK2', 'JK5', 'JK8'],
        nw: [-39.1, 24.5],
        se: [-41.8, 27.5],
        sub: [
            [2, 3],
            [5, 6],
            [8, 9],
        ],
        o: 'v',
    },
    { ids: ['JK3', 'JK6', 'JK9'], nw: [-39.1, 27.5], se: [-41.8, 32], o: 'v' },
    {
        ids: ['JS2', 'JS5'],
        nw: [-47.2, 24.5],
        se: [-49.9, 27.5],
        sub: [
            [2, 3],
            [5, 6],
            [8, 9],
        ],
        o: 'v',
    },
    { ids: ['JS3', 'JS6', 'JS9'], nw: [-47.2, 27.5], se: [-49.9, 32], o: 'v' },
    {
        ids: [
            'JS7599',
            'JS7833',
            'JS7836',
            'JS7839',
            'JS7863',
            'JS7866',
            'JS7869',
            'JS7893',
            'JS7896',
            'JS7899',
        ],
        nw: [-54.3, 21.2],
        se: [-54.4, 21.5],
        o: 'v',
    },
    {
        ids: ['JS767', 'JS768', 'JS769', 'JS847', 'JS848', 'JS849'],
        nw: [-54.3, 21.5],
        se: [-54.4, 22],
        sub: [[1, 2, 3]],
        o: 'h',
    },
    { ids: ['JS79', 'JS87'], nw: [-54.4, 21.5], se: [-55.3, 23], o: 'h' },
    { ids: ['JS82', 'JS85', 'JS88'], nw: [-52.6, 24.5], se: [-53.5, 26], o: 'v' },
    { ids: ['JS83', 'JS86', 'JS89'], nw: [-52.6, 26], se: [-53.5, 27.5], o: 'v' },
    {
        ids: [
            'KY231',
            'KY234',
            'KY237',
            'KY261',
            'KY264',
            'KY267',
            'KY291',
            'KY294',
            'KY297',
            'KY531',
            'KY534',
            'KY537',
            'KY561',
            'KY564',
            'KY567',
            'KY591',
            'KY594',
            'KY597',
            'KY831',
            'KY834',
            'KY837',
            'KY861',
            'KY864',
        ],
        nw: [-31, 22.1],
        se: [-31.3, 22.2],
        sub: [[3], [6], [9]],
        o: 'v',
    },
    {
        ids: [
            'KY232',
            'KY235',
            'KY238',
            'KY262',
            'KY265',
            'KY268',
            'KY292',
            'KY295',
            'KY298',
            'KY532',
            'KY535',
            'KY538',
            'KY562',
            'KY565',
            'KY568',
            'KY592',
            'KY595',
            'KY598',
            'KY832',
            'KY835',
            'KY838',
            'KY862',
            'KY865',
        ],
        nw: [-31, 22.2],
        se: [-31.3, 22.6],
        o: 'v',
    },
    {
        ids: [
            'KY233',
            'KY236',
            'KY239',
            'KY263',
            'KY266',
            'KY269',
            'KY293',
            'KY296',
            'KY299',
            'KY533',
            'KY536',
            'KY539',
            'KY563',
            'KY566',
            'KY569',
            'KY593',
            'KY596',
            'KY599',
            'KY833',
            'KY836',
            'KY839',
            'KY863',
            'KY866',
        ],
        nw: [-31, 22.6],
        se: [-31.3, 23],
        o: 'v',
    },
    { ids: ['KY3', 'KY6'], nw: [-31, 23], se: [-33.7, 26.6], o: 'v' },
    {
        ids: ['KY868', 'KY869', 'KY947', 'KY948', 'KY949'],
        nw: [-37.9, 22.2],
        se: [-38.1, 22.6],
        sub: [
            [4, 5, 6],
            [7, 8, 9],
        ],
        o: 'h',
    },
    { ids: ['KY91', 'KY92', 'KY93'], nw: [-36.4, 23], se: [-37.3, 24.2], o: 'h' },
    {
        ids: ['KY941', 'KY942', 'KY943', 'KY951'],
        nw: [-37.3, 23],
        se: [-37.6, 23.4],
        o: 'h',
    },
    {
        ids: ['KY944', 'KY945', 'KY946', 'KY954'],
        nw: [-37.6, 23],
        se: [-37.9, 23.4],
        o: 'h',
    },
    {
        ids: ['KY952', 'KY955', 'KY958', 'KY982', 'KY985', 'KY988'],
        nw: [-37.3, 24.6],
        se: [-37.6, 25],
        o: 'v',
    },
    {
        ids: ['KY953', 'KY956', 'KY959', 'KY983', 'KY986', 'KY989'],
        nw: [-37.3, 25],
        se: [-37.6, 25.4],
        o: 'v',
    },
    {
        ids: ['KY9571', 'KY9572', 'KY9573'],
        nw: [-37.9, 24.2],
        se: [-38, 24.33333333],
        o: 'h',
    },
    {
        ids: ['KY9574', 'KY9575', 'KY9576'],
        nw: [-38, 24.2],
        se: [-38.1, 24.33333333],
        o: 'h',
    },
    { ids: ['KY96', 'KY99'], nw: [-37.3, 25.4], se: [-38.2, 26.6], o: 'v' },
    {
        ids: ['KY981', 'KY984', 'KY987'],
        nw: [-38.2, 24.5],
        se: [-38.5, 24.6],
        sub: [[3], [6], [9]],
        o: 'v',
    },
    {
        ids: ['CT5', 'CT8', 'MA2', 'MA5', 'MA8'],
        nw: [39.2, 41],
        se: [36.5, 44.6],
        o: 'v',
    },
    {
        ids: ['CT6', 'CT9', 'MA3', 'MA6', 'MA9'],
        nw: [39.2, 44.6],
        se: [36.5, 48.2],
        o: 'v',
    },
    {
        ids: [
            'CT19',
            'CT43',
            'CT46',
            'CT49',
            'CT73',
            'CT76',
            'CT79',
            'MA13',
            'MA16',
            'MA19',
            'MA43',
            'MA46',
            'MA49',
            'MA73',
            'MA76',
            'MA79',
        ],
        nw: [40.1, 39.8],
        se: [39.2, 41],
        o: 'v',
    },
    {
        ids: [
            'CT181',
            'CT184',
            'CT187',
            'CT421',
            'CT424',
            'CT427',
            'CT451',
            'CT454',
            'CT457',
            'CT481',
            'CT484',
            'CT487',
            'CT721',
            'CT724',
            'CT727',
            'CT751',
            'CT754',
            'CT757',
            'CT781',
            'CT784',
            'CT787',
            'MA121',
            'MA124',
            'MA127',
            'MA151',
            'MA154',
            'MA157',
            'MA181',
            'MA184',
            'MA187',
            'MA421',
            'MA424',
            'MA427',
            'MA451',
            'MA454',
            'MA457',
            'MA481',
            'MA484',
            'MA487',
            'MA721',
            'MA724',
            'MA727',
            'MA751',
            'MA754',
        ],
        nw: [40.1, 38.9],
        se: [39.8, 39],
        sub: [[3], [6], [9]],
        o: 'v',
    },
    {
        ids: [
            'CT182',
            'CT185',
            'CT188',
            'CT422',
            'CT425',
            'CT428',
            'CT452',
            'CT455',
            'CT458',
            'CT482',
            'CT485',
            'CT488',
            'CT722',
            'CT725',
            'CT728',
            'CT752',
            'CT755',
            'CT758',
            'CT782',
            'CT785',
            'CT788',
            'MA122',
            'MA125',
            'MA128',
            'MA152',
            'MA155',
            'MA158',
            'MA182',
            'MA185',
            'MA188',
            'MA422',
            'MA425',
            'MA428',
            'MA452',
            'MA455',
            'MA458',
            'MA482',
            'MA485',
            'MA488',
            'MA722',
            'MA725',
            'MA728',
            'MA752',
            'MA755',
        ],
        nw: [40.1, 39],
        se: [39.8, 39.4],
        o: 'v',
    },
    {
        ids: [
            'CT183',
            'CT186',
            'CT189',
            'CT423',
            'CT426',
            'CT429',
            'CT453',
            'CT456',
            'CT459',
            'CT483',
            'CT486',
            'CT489',
            'CT723',
            'CT726',
            'CT729',
            'CT753',
            'CT756',
            'CT759',
            'CT783',
            'CT786',
            'CT789',
            'MA123',
            'MA126',
            'MA129',
            'MA153',
            'MA156',
            'MA159',
            'MA183',
            'MA186',
            'MA189',
            'MA423',
            'MA426',
            'MA429',
            'MA453',
            'MA456',
            'MA459',
            'MA483',
            'MA486',
            'MA489',
            'MA723',
            'MA726',
            'MA729',
            'MA753',
            'MA756',
        ],
        nw: [40.1, 39.4],
        se: [39.8, 39.8],
        o: 'v',
    },
    { ids: ['MA7573', 'MA7576'], nw: [26.9, 38.9], se: [26.8, 39], o: 'v' },
    {
        ids: ['MA7591', 'MA7592', 'MA7593'],
        nw: [26.9, 39.4],
        se: [26.8, 39.53333333],
        o: 'h',
    },
    {
        ids: ['MA7594', 'MA7595', 'MA7596'],
        nw: [26.8, 39.4],
        se: [26.7, 39.53333333],
        o: 'h',
    },
    {
        ids: ['MA758'],
        nw: [26.9, 39],
        se: [26.7, 39.4],
        sub: [
            [4, 5, 6],
            [7, 8, 9],
        ],
        o: 'v',
    },
    {
        ids: [
            'MA7598',
            'MA7832',
            'MA7835',
            'MA7838',
            'MA7862',
            'MA7865',
            'MA7868',
            'MA7892',
            'MA7895',
            'MA7898',
        ],
        nw: [26.7, 39.53333333],
        se: [26.6, 39.6666666],
        o: 'v',
    },
    {
        ids: [
            'MA7599',
            'MA7833',
            'MA7836',
            'MA7839',
            'MA7863',
            'MA7866',
            'MA7869',
            'MA7893',
            'MA7896',
            'MA7899',
        ],
        nw: [26.7, 39.6666666],
        se: [26.6, 39.8],
        o: 'v',
    },
    {
        ids: [
            'MA7597',
            'MA7831',
            'MA7834',
            'MA7837',
            'MA7861',
            'MA7864',
            'MA7867',
            'MA7891',
            'MA7894',
            'MA7897',
        ],
        nw: [26.7, 39.5],
        se: [26.6, 39.53333333],
        o: 'v',
    },
    {
        ids: ['NC1', 'NC4', 'NC7'],
        nw: [66.2, 167],
        se: [63.5, 170.6],
        sub: [
            [1, 2],
            [4, 5],
            [7, 8],
        ],
        o: 'v',
    },
    {
        ids: ['PS1', 'PS4', 'PS7', 'PV1', 'PV4', 'PV7'],
        nw: [-30, -100],
        se: [-32.7, -97.9],
        sub: [
            [2, 3],
            [5, 6],
            [8, 9],
        ],
        o: 'v',
    },
    {
        ids: ['PS2', 'PS5', 'PS8', 'PV2', 'PV5', 'PV8'],
        nw: [-30, -97.9],
        se: [-32.7, -94.3],
        o: 'v',
    },
    {
        ids: ['PS3', 'PS6', 'PS9', 'PV3', 'PV6', 'PV9'],
        nw: [-30, -94.3],
        se: [-32.7, -90.7],
        o: 'v',
    },
    {
        ids: [
            'QJ1',
            'QJ4',
            'QJ7',
            'RB1',
            'RB4',
            'RB7',
            'RU1',
            'RU4',
            'RU7',
            'SN1',
            'SN4',
            'SN7',
            'TF1',
            'TF4',
            'TF7',
            'TZ1',
            'TZ4',
            'TZ7',
        ],
        nw: [25.7, 167],
        se: [23, 169.7],
        o: 'v',
    },
    {
        ids: [
            'QJ2',
            'QJ5',
            'QJ8',
            'RB2',
            'RB5',
            'RB8',
            'RU2',
            'RU5',
            'RU8',
            'SN2',
            'SN5',
            'SN8',
            'TF2',
            'TF5',
            'TF8',
            'TZ2',
            'TZ5',
            'TZ8',
        ],
        nw: [25.7, 169.7],
        se: [23, 170.6],
        sub: [[1], [4], [7]],
        o: 'v',
    },
    { ids: ['QU1', 'QU4', 'QU7'], nw: [25.7, -108.4], se: [23, -105.7], o: 'v' },
    { ids: ['QU2', 'QU5', 'QU8'], nw: [25.7, -105.7], se: [23, -102.7], o: 'v' },
    { ids: ['QU3', 'QU6'], nw: [25.7, -102.7], se: [23, -100], o: 'v' },
    {
        ids: ['QU91', 'QU94', 'QU97'],
        nw: [20.3, -102.7],
        se: [19.4, -101.8],
        o: 'v',
    },
    {
        ids: ['QU92', 'QU95', 'QU98'],
        nw: [20.3, -101.8],
        se: [19.4, -100.9],
        o: 'v',
    },
    { ids: ['QU93'], nw: [20.3, -100.9], se: [19.4, -100], o: 'v' },
    {
        ids: ['QU961', 'QU962', 'QU963'],
        nw: [19.4, -100.9],
        se: [19.1, -100.6],
        o: 'h',
    },
    {
        ids: ['QU964', 'QU965', 'QU966'],
        nw: [19.1, -100.9],
        se: [18.8, -100.6],
        o: 'h',
    },
    {
        ids: ['QU967', 'QU968', 'QU969'],
        nw: [18.8, -100.9],
        se: [18.5, -100.6],
        sub: [
            [4, 5, 6],
            [7, 8, 9],
        ],
        o: 'h',
    },
    {
        ids: ['RN1', 'RN4', 'RN7', 'SF1', 'SF4', 'SF7'],
        nw: [17.6, -108.4],
        se: [14.9, -105.7],
        o: 'v',
    },
    {
        ids: ['RN2', 'RN5', 'RN8', 'SF2', 'SF5', 'SF8'],
        nw: [17.6, -105.7],
        se: [14.9, -102.7],
        o: 'v',
    },
    {
        ids: ['RN3', 'RN6', 'RN9', 'SF3', 'SF6', 'SF9'],
        nw: [17.6, -102.7],
        se: [14.9, -100.9],
        sub: [
            [1, 2],
            [4, 5],
            [7, 8],
        ],
        o: 'v',
    },
    { ids: ['SY1', 'SY4', 'SY7'], nw: [1.4, -108.4], se: [-1.3, -105.7], o: 'v' },
    { ids: ['SY2', 'SY5', 'SY8'], nw: [1.4, -105.7], se: [-1.3, -102.7], o: 'v' },
    {
        ids: ['SY3', 'SY6'],
        nw: [1.4, -102.7],
        se: [-1.3, -100.9],
        sub: [
            [1, 2],
            [4, 5],
            [7, 8],
        ],
        o: 'v',
    },
    {
        ids: ['SY91', 'SY94', 'SY97'],
        nw: [-4, -102.7],
        se: [-4.9, -101.8],
        o: 'v',
    },
    {
        ids: ['SY92', 'SY95', 'SY98'],
        nw: [-4, -101.8],
        se: [-4.9, -100.9],
        o: 'v',
    },
    {
        ids: ['SY967', 'SY968', 'SY969'],
        nw: [-5.7, -100.9],
        se: [-5.8, -100.6],
        sub: [[1, 2, 3]],
        o: 'h',
    },
    { ids: ['SY99'], nw: [-5.8, -100.9], se: [-6.7, -100], o: 'v' },
    {
        ids: ['TR1', 'TR4', 'TR7', 'UL1', 'UL4', 'UL7'],
        nw: [-6.7, -108.4],
        se: [-9.4, -105.7],
        o: 'v',
    },
    {
        ids: ['TR2', 'TR5', 'TR8', 'UL2', 'UL5', 'UL8'],
        nw: [-6.7, -105.7],
        se: [-9.4, -102.7],
        o: 'v',
    },
    {
        ids: ['TR3', 'TR6', 'TR9', 'UL3', 'UL6', 'UL9'],
        nw: [-6.7, -102.7],
        se: [-9.4, -100],
        o: 'v',
    },
    {
        ids: ['VA1', 'VA4'],
        nw: [-22.9, -103],
        se: [-25.6, -100],
        sub: [
            [1, 2],
            [4, 5],
            [7, 8],
        ],
        o: 'v',
    },
    {
        ids: ['VA7'],
        nw: [-28.3, -103],
        se: [-30, -100],
        sub: [
            [4, 5],
            [7, 8],
        ],
        o: 'v',
    },
    {
        ids: ['VP1', 'VP4', 'VP7', 'WA1', 'WA4', 'WA7'],
        nw: [-30, -103],
        se: [-32.7, -100],
        sub: [
            [1, 2],
            [4, 5],
            [7, 8],
        ],
        o: 'v',
    },
    { ids: ['WM1', 'WM4', 'WM7'], nw: [-46.2, -103], se: [-48.9, -98.5], o: 'v' },
    {
        ids: ['WM2', 'WM5', 'WM8'],
        nw: [-46.2, -98.5],
        se: [-48.9, -97],
        sub: [[1], [4], [7]],
        o: 'v',
    },
    {
        ids: [
            'WV141',
            'WV142',
            'WV143',
            'WV151',
            'WV152',
            'WV153',
            'WV161',
            'WV162',
            'WV163',
        ],
        nw: [-55.3, -108.4],
        se: [-55.5, -107.8],
        sub: [
            [1, 2, 3],
            [4, 5, 6],
        ],
        o: 'h',
    },
    {
        ids: [
            'WV144',
            'WV145',
            'WV146',
            'WV154',
            'WV155',
            'WV156',
            'WV164',
            'WV165',
            'WV166',
        ],
        nw: [-55.5, -108.4],
        se: [-55.8, -107.8],
        o: 'h',
    },
    {
        ids: [
            'WV147',
            'WV148',
            'WV149',
            'WV157',
            'WV158',
            'WV159',
            'WV167',
            'WV168',
            'WV169',
        ],
        nw: [-55.8, -108.4],
        se: [-56.1, -107.8],
        o: 'h',
    },
    {
        ids: ['WV17', 'WV18', 'WV19'],
        nw: [-56.1, -108.4],
        se: [-57, -106.6],
        o: 'h',
    },
    { ids: ['WV2', 'WV3'], nw: [-54.3, -103], se: [-57, -97.6], o: 'h' },
    { ids: ['WV4', 'WV5', 'WV6'], nw: [-57, -108.4], se: [-59.7, -103], o: 'h' },
    {
        ids: ['WV7', 'WV8', 'WV9'],
        nw: [-59.7, -108.4],
        se: [-62.4, -103],
        o: 'h',
    },
    { ids: ['XG1', 'XG4', 'XG7'], nw: [74.3, 167], se: [71.6, 173.75], o: 'v' },
    {
        ids: ['XG2', 'XG5', 'XG8'],
        nw: [74.3, 173.75],
        se: [71.6, -179.5],
        o: 'v',
    },
    {
        ids: ['XG3', 'XG6', 'XG9'],
        nw: [74.3, -179.5],
        se: [71.6, -177.25],
        sub: [[1], [4], [7]],
        o: 'v',
    },
    { ids: ['XN1', 'XN2', 'XN3'], nw: [82.4, -103], se: [79.7, -94], o: 'h' },
    { ids: ['XN4', 'XN5'], nw: [79.7, -103], se: [77, -94], o: 'h' },
    { ids: ['XN61', 'XN62', 'XN63'], nw: [79.7, -85], se: [78.8, -82], o: 'h' },
    { ids: ['XN64', 'XN65', 'XN66'], nw: [78.8, -85], se: [77.9, -82], o: 'h' },
    {
        ids: [
            'XN671',
            'XN672',
            'XN673',
            'XN681',
            'XN682',
            'XN683',
            'XN691',
            'XN692',
            'XN693',
        ],
        nw: [77.9, -85],
        se: [77.6, -84],
        o: 'h',
    },
    {
        ids: [
            'XN674',
            'XN675',
            'XN676',
            'XN684',
            'XN685',
            'XN686',
            'XN694',
            'XN695',
            'XN696',
        ],
        nw: [77.6, -85],
        se: [77.3, -84],
        o: 'h',
    },
    {
        ids: [
            'XN677',
            'XN678',
            'XN679',
            'XN687',
            'XN688',
            'XN689',
            'XN697',
            'XN698',
            'XN699',
        ],
        nw: [77.3, -85],
        se: [77.1, -84],
        sub: [
            [4, 5, 6],
            [7, 8, 9],
        ],
        o: 'h',
    },
    { ids: ['XN7', 'XN8'], nw: [77, -103], se: [74.3, -94], o: 'h' },
    { ids: ['XR1', 'XR4', 'XR7'], nw: [74.3, -96.25], se: [71.6, -89.5], o: 'v' },
    {
        ids: ['XR2', 'XR5', 'XR8'],
        nw: [74.3, -89.5],
        se: [71.6, -85],
        sub: [
            [1, 2],
            [4, 5],
            [7, 8],
        ],
        o: 'v',
    },
    { ids: ['YB8', 'YB9'], nw: [85.1, 86], se: [82.4, 106.25], o: 'h' },
    {
        ids: ['YC7', 'YC8', 'YC9', 'YD7', 'YD8'],
        nw: [85.1, 126.5],
        se: [82.4, 146.75],
        o: 'h',
    },
    { ids: ['YE7', 'YE8'], nw: [85.1, -116.5], se: [84.2, -96.25], o: 'h' },
    {
        ids: ['YD93', 'YD96', 'YD99'],
        nw: [85.1, -118.75],
        se: [84.2, -116.5],
        sub: [[1], [4], [7]],
        o: 'v',
    },
    {
        ids: ['YD91', 'YD94', 'YD97'],
        nw: [85.1, -132.25],
        se: [84.2, -125.5],
        o: 'v',
    },
    {
        ids: ['YD92', 'YD95', 'YD98'],
        nw: [85.1, -125.5],
        se: [84.2, -118.75],
        o: 'v',
    },
];
//# sourceMappingURL=data.js.map